import React, { useMemo, useState } from "react";
import {
  Query,
  QueryAdminRequestsAdminArgs,
  Mutation,
  MutationAcceptAdminRequestArgs,
  MutationRefuseAdminRequestArgs,
  AdminRequestStatus,
  AdminRequestValidationMethod
} from "@td/codegen-ui";
import Pagination from "@codegouvfr/react-dsfr/Pagination";
import { useMutation, useQuery } from "@apollo/client";
import {
  ACCEPT_ADMIN_REQUEST,
  ADMIN_REQUESTS_ADMIN,
  REFUSE_ADMIN_REQUEST
} from "../../Apps/common/queries/adminRequest/adminRequest";
import { formatDateViewDisplay } from "../../Apps/Companies/common/utils";
import Button from "@codegouvfr/react-dsfr/Button";
import toast from "react-hot-toast";
import { getStatusBadge } from "../../Apps/Companies/CompanyManage/CompanyAdminRequest/CompanyAdminRequestsTable";
import Input from "@codegouvfr/react-dsfr/Input";
import z from "zod";
import { debounce, isDefinedStrict } from "../../common/helper";

const filtersSchema = z.object({
  siret: z
    .string()
    .optional()
    .transform(val => (isDefinedStrict(val) ? val : undefined)),
  date: z.coerce
    .date()
    .optional()
    .transform(val => (val ? new Date(val) : undefined))
});

export type ZodFilters = z.infer<typeof filtersSchema>;

const getMethod = (method: AdminRequestValidationMethod) => {
  switch (method) {
    case AdminRequestValidationMethod.RequestAdminApproval:
      return "Administrateurs";
    case AdminRequestValidationMethod.RequestCollaboratorApproval:
      return "Collaborateur";
    case AdminRequestValidationMethod.SendMail:
      return "Courrier";
  }
};

export const AdminRequests = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [siret, setSiret] = useState<string | undefined>(undefined);
  const [date, setDate] = useState<Date | undefined>(undefined);

  const PAGE_SIZE = 50;

  const { data, loading, refetch } = useQuery<
    Pick<Query, "adminRequestsAdmin">,
    QueryAdminRequestsAdminArgs
  >(ADMIN_REQUESTS_ADMIN, {
    fetchPolicy: "network-only",
    variables: {
      input: {
        skip: 0,
        first: PAGE_SIZE
      }
    }
  });

  const [acceptAdminRequest, { loading: loadingAccept }] = useMutation<
    Pick<Mutation, "acceptAdminRequest">,
    MutationAcceptAdminRequestArgs
  >(ACCEPT_ADMIN_REQUEST);

  const [refuseAdminRequest, { loading: loadingRefuse }] = useMutation<
    Pick<Mutation, "refuseAdminRequest">,
    MutationRefuseAdminRequestArgs
  >(REFUSE_ADMIN_REQUEST);

  const totalCount = data?.adminRequestsAdmin.totalCount;
  const requests = data?.adminRequestsAdmin.edges.map(edge => edge.node) ?? [];

  const pageCount = totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 0;

  const gotoPage = (page: number) => {
    setPageIndex(page);

    refetch({
      input: {
        skip: page * PAGE_SIZE,
        first: PAGE_SIZE
      }
    });
  };

  // Filters form

  const debouncedSearch = useMemo(
    () =>
      debounce(params => {
        try {
          // Validate params
          const parsed = filtersSchema.parse(params);

          // Reset page index
          setPageIndex(0);

          refetch({
            input: {
              skip: 0,
              first: PAGE_SIZE,
              siret: parsed.siret,
              date: parsed.date?.toISOString()
            }
          });
        } catch (err: any) {
          console.error(err);
          return;
        }
      }, 500),
    [refetch]
  );

  return (
    <div>
      <h3 className="fr-sr-only">Demandes administrateur</h3>

      {/* Filters */}
      <div className="fr-grid-row fr-grid-row--top">
        <div className="fr-col-sm-6 fr-col-lg-4 fr-col-xl-3 fr-pr-2w">
          <Input
            label={false}
            nativeInputProps={{
              type: "text",
              placeholder: "SIRET",
              onChange: e => {
                const value = isDefinedStrict(e.target.value)
                  ? e.target.value
                  : undefined;
                setSiret(value);
                debouncedSearch({
                  siret: value,
                  date
                });
              }
            }}
          />
        </div>

        <div className="fr-col-sm-6 fr-col-lg-4 fr-col-xl-3">
          <Input
            label={false}
            nativeInputProps={{
              type: "date",
              onChange: e => {
                const value = isDefinedStrict(e.target.value)
                  ? new Date(e.target.value)
                  : undefined;
                setDate(value);
                debouncedSearch({
                  siret,
                  date: value
                });
              }
            }}
          />
        </div>
      </div>

      <div className="fr-table--sm fr-table fr-table" id="table-sm-component">
        <div className="fr-table__wrapper">
          <div className="fr-table__container">
            <div className="fr-table__content">
              <table className="requests-table">
                <thead>
                  <tr>
                    <th scope="col">Utilisateur</th>
                    <th className="fr-py-4v table-col" scope="col">
                      Établissement
                    </th>
                    <th scope="col">Méthode</th>
                    <th scope="col">Date de la demande</th>
                    <th scope="col">Statut</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(request => {
                    const {
                      id,
                      company,
                      user,
                      createdAt,
                      status,
                      validationMethod
                    } = request;

                    return (
                      <tr key={id}>
                        <td>{user.name}</td>
                        <td
                          className="fr-py-4v"
                          aria-describedby={`company-name-${company.orgId}`}
                        >
                          {`${company.name} - ${company.orgId}`}
                        </td>
                        <td>{getMethod(validationMethod)}</td>
                        <td>{formatDateViewDisplay(createdAt)}</td>
                        <td>{getStatusBadge(status)}</td>
                        <td>
                          {status === AdminRequestStatus.Pending && (
                            <>
                              <Button
                                disabled={loadingAccept || loadingRefuse}
                                priority="primary"
                                className="fr-mr-2w"
                                onClick={() => {
                                  acceptAdminRequest({
                                    variables: {
                                      input: { adminRequestId: id }
                                    },
                                    onCompleted: () => {
                                      toast.success("Demande accordée");
                                    },
                                    onError: e => toast.error(e.message)
                                  });
                                }}
                                type="button"
                              >
                                Accepter
                              </Button>

                              <Button
                                priority="secondary"
                                type="submit"
                                disabled={loadingAccept || loadingRefuse}
                                onClick={() => {
                                  refuseAdminRequest({
                                    variables: { adminRequestId: id },
                                    onCompleted: () => {
                                      toast.success("Demande refusée");
                                    },
                                    onError: e => toast.error(e.message)
                                  });
                                }}
                              >
                                Refuser
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {loading && <p className="fr-m-4v">Chargement...</p>}
                  {!loading && !requests.length && (
                    <p className="fr-m-4v">Aucune demande</p>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div style={{ display: "flex" }}>
          <Pagination
            style={{ margin: "0 auto" }}
            showFirstLast
            count={pageCount}
            defaultPage={pageIndex + 1}
            getPageLinkProps={pageNumber => ({
              onClick: event => {
                event.preventDefault();
                gotoPage(pageNumber - 1);
              },
              href: "#",
              key: `pagination-link-${pageNumber}`
            })}
            className={"fr-mt-1w"}
          />
        </div>
      </div>
    </div>
  );
};
